import React, { useState, useCallback } from "react";
import adImage from "../assets/easter-buffet.png";
import ErrorModal from "../components/ErrorModal";
import SpinnerOverlay from "../components/SpinnerOverlay";
import { useApi } from "../utils/Api";

export default function EasterBuffet() {
  const { apiFetch } = useApi();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    timeSlot: "",
    adults: 1,
    children: 0,
  });

  const [loading, setLoading] = useState(false);
  const [slotLoading, setSlotLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [slotStatus, setSlotStatus] = useState([]);

  const sessions = {
    "2026-04-03": {
      label: "Fri, 3rd April 2026",
      times: [
        { label: "12:00 - 2:00pm", value: "12:00-14:00" },
        { label: "3:00 - 5:00pm", value: "15:00-17:00" },
        { label: "6:00 - 8:00pm", value: "18:00-20:00" },
      ],
    },
    "2026-04-04": {
      label: "Sat, 4th April 2026",
      times: [
        { label: "12:00 - 2:00pm", value: "12:00-14:00" },
        { label: "3:00 - 5:00pm", value: "15:00-17:00" },
        { label: "6:00 - 8:00pm", value: "18:00-20:00" },
      ],
    },
    "2026-04-05": {
      label: "Sun, 5th April 2026",
      times: [
        { label: "3:00 - 5:00pm", value: "15:00-17:00" },
        { label: "6:00 - 8:00pm", value: "18:00-20:00" },
      ],
    },
  };

  // FRIDAY URGENCY OVERRIDE
  const getFridayOverride = (slotValue) => {
    if (formData.date !== "2026-04-03") return null;

    const map = {
      "12:00-14:00": { label: "Sold out", remaining: 0 },
      "15:00-17:00": { label: "Sold out", remaining: 0 },
      "18:00-20:00": { label: "1 child seat left", remaining: 1 },
    };

    return map[slotValue] || null;
  };

  const fetchSlotStatus = useCallback(
    async (date) => {
      if (!date) return;

      setSlotLoading(true);
      setSlotStatus([]);

      setFormData((prev) => ({
        ...prev,
        timeSlot: "",
      }));

      try {
        const res = await apiFetch("get-easter-slots", { date });
        setSlotStatus(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSlotLoading(false);
      }
    },
    [apiFetch],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const totalGuests =
    Number(formData.adults || 0) + Number(formData.children || 0);

  const depositAmount = totalGuests * 5;

  const isFormValid =
    formData.name.trim() &&
    formData.phone.trim() &&
    formData.email.trim() &&
    formData.date &&
    formData.timeSlot &&
    Number(formData.adults) >= 1;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) return;

    if (Number(formData.children) > 2) {
      setErrorMessage("Maximum of 2 children allowed per booking.");
      return;
    }

    if (totalGuests > 20) {
      setErrorMessage("Maximum 20 guests per slot.");
      return;
    }

    const selectedSlot = slotStatus.find(
      (s) => s.time === formData.timeSlot,
    );

    if (!selectedSlot) {
      setErrorMessage("Invalid time slot selected.");
      return;
    }

    if (selectedSlot.isPast) {
      setErrorMessage("This time slot is no longer available.");
      return;
    }

    if (selectedSlot.isFull) {
      setErrorMessage("This slot is fully booked.");
      return;
    }

    setLoading(true);

    try {
      const result = await apiFetch("create-easter-checkout", {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        date: formData.date,
        timeSlot: formData.timeSlot,
        numberOfAdults: formData.adults,
        numberOfChildren: formData.children,
        depositAmount,
      });

      if (result.success && result.url) {
        window.location.href = result.url;
      } else {
        setErrorMessage(result.message || "Failed to start payment.");
      }
    } catch (err) {
      setErrorMessage("Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 px-4 py-10">
      {loading && <SpinnerOverlay />}
      {errorMessage && (
        <ErrorModal message={errorMessage} onClose={() => setErrorMessage("")} />
      )}

      <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">

        {/* IMAGE */}
        <div className="overflow-hidden rounded-3xl bg-white shadow-lg">
          <img src={adImage} alt="Easter Buffet" className="w-full object-cover" />
        </div>

        {/* FORM */}
        <div className="rounded-3xl bg-white p-8 shadow-lg">
          <h1 className="mb-2 text-3xl font-bold text-slate-900">
            Easter Buffet Booking
          </h1>

          <p className="mb-4 text-slate-600">
            Reserve your seat for our exclusive Easter buffet experience.
          </p>

          <div className="mb-6 rounded-xl bg-amber-50 p-4 text-sm text-amber-800 border border-amber-200">
            <p className="font-semibold">Important:</p>
            <p>A £5 per guest deposit is required to confirm your booking.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* NAME */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 w-full border px-4 py-3"
              />
            </div>

            {/* PHONE */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 w-full border px-4 py-3"
              />
            </div>

            {/* EMAIL */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 w-full border px-4 py-3"
              />
            </div>

            {/* DATE */}
            <div>
              <label className="block text-sm font-medium text-slate-800">
                Select Date *
              </label>

              <select
                name="date"
                value={formData.date}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    date: value,
                    timeSlot: "",
                  }));
                  fetchSlotStatus(value);
                }}
                className="mt-1 w-full border px-4 py-3"
              >
                <option value="">-- Choose Date --</option>
                {Object.entries(sessions).map(([date, data]) => (
                  <option key={date} value={date}>
                    {data.label}
                  </option>
                ))}
              </select>
            </div>

            {/* TIME SLOT */}
            {formData.date && (
              <div>
                <label className="block text-sm font-medium text-slate-800">
                  Select Time Slot *
                </label>

                {slotLoading ? (
                  <p className="text-sm text-slate-500 mt-2">
                    Loading available slots...
                  </p>
                ) : (
                  <select
                    name="timeSlot"
                    value={formData.timeSlot}
                    onChange={handleChange}
                    className="mt-1 w-full border px-4 py-3"
                  >
                    <option value="">-- Choose Slot --</option>

                    {sessions[formData.date].times.map((slot) => {
                      const override = getFridayOverride(slot.value);
                      const status = slotStatus.find(
                        (s) => s.time === slot.value,
                      );

                      const isSoldOut =
                        override
                          ? override.remaining === 0
                          : status?.isFull || status?.isPast;

                      return (
                        <option
                          key={slot.value}
                          value={slot.value}
                          disabled={isSoldOut || formData.date === "2026-04-03"}
                        >
                          {slot.label}{" "}
                          {override ? (
                            `(${override.label})`
                          ) : status ? (
                            status.isFull
                              ? "(Sold out)"
                              : `(${status.remaining} left)`
                          ) : null}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            )}

            {/* GUESTS */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-2">
                Number of Guests *
              </label>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-600 mb-1">
                    Adults
                  </label>
                  <input
                    type="number"
                    name="adults"
                    min="1"
                    value={formData.adults}
                    onChange={handleChange}
                    className="w-full border px-4 py-3"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-600 mb-1">
                    Children
                  </label>
                  <input
                    type="number"
                    name="children"
                    min="0"
                    value={formData.children}
                    onChange={handleChange}
                    className="w-full border px-4 py-3"
                  />
                </div>
              </div>
            </div>

            {/* BUTTON */}
            <button
              type="submit"
              disabled={!isFormValid || loading}
              className="w-full bg-primary py-3 font-semibold text-white disabled:opacity-60"
            >
              Pay Online (£{depositAmount})
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}